import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  TextField
} from '@mui/material';
import { CheckCircle, Cancel, Schedule, Warning } from '@mui/icons-material';
import { attendanceApi, AttendanceRecord } from '../../services/attendanceService';
import { format, subDays } from 'date-fns';

interface AttendanceHistoryProps {
  defaultDays?: number;
}

export function AttendanceHistory({ defaultDays = 30 }: AttendanceHistoryProps) {
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>(
    format(subDays(new Date(), defaultDays), 'yyyy-MM-dd')
  );
  const [endDate, setEndDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));

  useEffect(() => {
    loadHistory();
  }, [startDate, endDate]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await attendanceApi.getHistory(startDate, endDate);
      setRecords(data.records);
    } catch (err) {
      setError((err as Error).message || 'Failed to load attendance history');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string, isComplete: boolean) => {
    if (!isComplete) {
      return <Schedule color="warning" fontSize="small" />;
    }
    switch (status) {
      case 'PRESENT':
        return <CheckCircle color="success" fontSize="small" />;
      case 'LATE':
        return <Warning color="warning" fontSize="small" />;
      case 'HALF_DAY':
        return <Schedule color="info" fontSize="small" />;
      case 'ABSENT':
        return <Cancel color="error" fontSize="small" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (status) {
      case 'PRESENT':
        return 'success';
      case 'LATE':
        return 'warning';
      case 'HALF_DAY':
        return 'info';
      case 'ABSENT':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatTime = (timeString?: string) => {
    if (!timeString) return '-';
    return format(new Date(timeString), 'hh:mm a');
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'EEE, MMM d');
  };

  // Calculate summary
  const summary = {
    total: records.length,
    present: records.filter(r => r.status === 'PRESENT').length,
    late: records.filter(r => r.status === 'LATE').length,
    absent: records.filter(r => r.status === 'ABSENT').length,
    totalHours: records.reduce((sum, r) => sum + (r.workingMinutes || 0), 0) / 60
  };

  return (
    <Box>
      {/* Date Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          label="From"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="To"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      {/* Summary */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Paper sx={{ px: 2, py: 1, flex: 1, minWidth: 100 }}>
          <Typography variant="caption" color="text.secondary">Total Days</Typography>
          <Typography variant="h6">{summary.total}</Typography>
        </Paper>
        <Paper sx={{ px: 2, py: 1, flex: 1, minWidth: 100 }}>
          <Typography variant="caption" color="text.secondary">Present</Typography>
          <Typography variant="h6" color="success.main">{summary.present}</Typography>
        </Paper>
        <Paper sx={{ px: 2, py: 1, flex: 1, minWidth: 100 }}>
          <Typography variant="caption" color="text.secondary">Late</Typography>
          <Typography variant="h6" color="warning.main">{summary.late}</Typography>
        </Paper>
        <Paper sx={{ px: 2, py: 1, flex: 1, minWidth: 100 }}>
          <Typography variant="caption" color="text.secondary">Total Hours</Typography>
          <Typography variant="h6">{summary.totalHours.toFixed(1)}h</Typography>
        </Paper>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : records.length === 0 ? (
        <Alert severity="info">No attendance records found for the selected period.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Check In</TableCell>
                <TableCell>Check Out</TableCell>
                <TableCell>Hours</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((record) => (
                <TableRow key={record.id}>
                  <TableCell>{formatDate(record.date)}</TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(record.status, record.isComplete)}
                      label={record.isComplete ? record.status : 'In Progress'}
                      color={getStatusColor(record.status)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{formatTime(record.checkInTime)}</TableCell>
                  <TableCell>{formatTime(record.checkOutTime)}</TableCell>
                  <TableCell>{record.workingHours || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
